import React, { useState, useEffect, useMemo } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Button,
  LinearProgress,
  CircularProgress,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Badge,
  Divider,
  Fade,
  Zoom
} from '@material-ui/core'
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  FilterList as FilterIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  GetApp as ExportIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Euro as EuroIcon,
  AccessTime as TimeIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon
} from '@material-ui/icons'
import moment from 'moment'
import { tourApi } from '../api/tourApi'
import CustomPagination from './CustomPagination'

// Estilos inline para el componente de lista de tours

/**
 * Componente optimizado para listar tours con diseño mejorado
 * Mejoras implementadas:
 * - Diseño moderno con gradientes y sombras
 * - Animaciones suaves y transiciones
 * - Mejor organización visual de la información
 * - Estados de carga más elegantes
 * - Filtros avanzados con chips
 * - Estadísticas visuales
 */
const TourList = ({ 
  onProcessTour, 
  onViewTour, 
  onEditTour, 
  onDeleteTour,
  showPending = true 
}) => {
  const [tours, setTours] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 1000,
    total: 0
  })
  const [filters, setFilters] = useState({
    search: '',
    status: showPending ? 'pending' : 'all',
    dateFrom: '',
    dateTo: '',
    priceRange: 'all'
  })
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    processed: 0,
    cancelled: 0,
    totalRevenue: 0
  })

  // Cargar tours
  const loadTours = async (page = 1, limit = 1000) => {
    setLoading(true)
    setError(null)
    
    try {
      const response = await tourApi.getTours({
        limit,
        offset: (page - 1) * limit,
        ...filters
      })
      
      setTours(response.data)
      setPagination(prev => ({
        ...prev,
        page,
        total: (response.pagination && response.pagination.total) || response.data.length
      }))

      // Calcular estadísticas
      calculateStats(response.data)
    } catch (err) {
      setError('Error al cargar los tours')
      console.error('Error loading tours:', err)
    } finally {
      setLoading(false)
    }
  }

  // Calcular estadísticas
  const calculateStats = (toursData) => {
    const stats = {
      total: toursData.length,
      pending: 0,
      processed: 0,
      cancelled: 0,
      totalRevenue: 0
    }

    toursData.forEach(tour => {
      const status = getNestedValue(tour, 'rental_info.status')
      const price = parseFloat(getNestedValue(tour, 'rental_info.rental_price', 0))
      
      switch (parseInt(status, 10)) {
        case 0:
          stats.pending++
          break
        case 1:
          stats.processed++
          stats.totalRevenue += price
          break
        case 3:
          stats.cancelled++
          break
      }
    })

    setStats(stats)
  }

  // Cargar tours al montar y cuando cambien los filtros
  useEffect(() => {
    loadTours(1, pagination.limit)
  }, [filters])

  // Manejar cambio de página
  const handlePageChange = (event, newValue) => {
    loadTours(newValue, pagination.limit)
  }

  // Manejar filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }))
  }

  // Función para obtener el texto del status
  const getStatusText = (status) => {
    const statusNum = parseInt(status, 10)
    
    switch (statusNum) {
      case 0:
        return 'Pending'
      case 1:
        return 'Processed'
      case 3:
        return 'Cancelled'
      default:
        return 'Unknown'
    }
  }

  // Función para obtener el color del status
  const getStatusColor = (status) => {
    const statusNum = parseInt(status, 10)
    
    switch (statusNum) {
      case 0:
        return 'warning'
      case 1:
        return 'primary'
      case 3:
        return 'secondary'
      default:
        return 'default'
    }
  }

  // Formatear fecha
  const formatDate = (date) => {
    return moment(date).format('DD/MM/YYYY')
  }

  // Formatear precio
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price || 0)
  }

  // Función helper para obtener valor seguro de objeto anidado
  const getNestedValue = (obj, path, defaultValue = 'N/A') => {
    if (!obj) return defaultValue
    
    if (path === 'rental_info.status' && obj.rental_info) {
      return obj.rental_info.status
    }
    
    const keys = path.split('.')
    let current = obj
    for (const key of keys) {
      if (current && typeof current === 'object' && key in current) {
        current = current[key]
      } else {
        return defaultValue
      }
    }
    // Manejar correctamente valores 0, false, null y undefined
    if (current === null || current === undefined) {
      return defaultValue
    }
    return current
  }

  const handleUpdate = () => {
    loadTours(1, pagination.limit)
  }

  const handleExport = () => {
    const exportData = tours.map(tour => ({
      'Tour Rent ID': getNestedValue(tour, 'id'),
      'Booking ID': getNestedValue(tour, 'booking_info.id') || getNestedValue(tour, 'booking_info.booking_id'),
      'Tour Name': getNestedValue(tour, 'tour_info.tour_name'),
      'P4M': (tour.booking_info && (tour.booking_info.p4m === 1 || tour.booking_info.p4m === true)) ? 'Yes' : 'No',
      'Tour Start Date': getNestedValue(tour, 'tour_info.tour_start_date') ? 
        moment(getNestedValue(tour, 'tour_info.tour_start_date')).format('DD/MM/YYYY') : 'N/A',
      'Client': getNestedValue(tour, 'client_info.name'),
      'Submitted': getNestedValue(tour, 'booking_info.submitted_date') ? 
        moment(getNestedValue(tour, 'booking_info.submitted_date')).format('DD/MM/YYYY') : 'N/A',
      'Status': getStatusText(getNestedValue(tour, 'rental_info.status')),
      'Price': formatPrice(getNestedValue(tour, 'rental_info.rental_price', 0))
    }))

    const headers = Object.keys(exportData[0] || {})
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => headers.map(header => `"${row[header]}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `tours_export_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const handleViewDetails = (tour) => {
    if (onViewTour) {
      onViewTour(tour)
    }
  }

  return (
    <div style={{ 
      backgroundColor: '#fafafa', 
      minHeight: '100vh', 
      padding: '16px',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      {/* Header con estadísticas */}
    {/*   <Card style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderRadius: 16,
        marginBottom: 24,
        padding: 24
      }}>
        <CardContent>
          <Grid container spacing={24} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                {showPending ? 'Pending Tours' : 'Processed Tours'}
              </Typography>
              <Typography variant="body1" style={{ opacity: 0.9 }}>
                Manage and monitor your tour bookings
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <div style={{ 
                    borderRadius: 12, 
                    padding: 16, 
                    textAlign: 'center', 
                    transition: 'all 0.3s ease-in-out',
                    backgroundColor: 'rgba(255,255,255,0.1)' 
                  }}>
                    <Typography variant="h6" style={{ color: 'white' }}>
                      {stats.total}
                    </Typography>
                    <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Total Tours
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <div style={{ 
                    borderRadius: 12, 
                    padding: 16, 
                    textAlign: 'center', 
                    transition: 'all 0.3s ease-in-out',
                    backgroundColor: 'rgba(255,193,7,0.2)' 
                  }}>
                    <Typography variant="h6" style={{ color: '#ffc107' }}>
                      {stats.pending}
                    </Typography>
                    <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Pending
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <div style={{ 
                    borderRadius: 12, 
                    padding: 16, 
                    textAlign: 'center', 
                    transition: 'all 0.3s ease-in-out',
                    backgroundColor: 'rgba(40,167,69,0.2)' 
                  }}>
                    <Typography variant="h6" style={{ color: '#28a745' }}>
                      {stats.processed}
                    </Typography>
                    <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Processed
                    </Typography>
                  </div>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <div style={{ 
                    borderRadius: 12, 
                    padding: 16, 
                    textAlign: 'center', 
                    transition: 'all 0.3s ease-in-out',
                    backgroundColor: 'rgba(220,53,69,0.2)' 
                  }}>
                    <Typography variant="h6" style={{ color: '#dc3545' }}>
                      {stats.cancelled}
                    </Typography>
                    <Typography variant="caption" style={{ color: 'rgba(255,255,255,0.8)' }}>
                      Cancelled
                    </Typography>
                  </div>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
 */}
      {/* Filtros mejorados */}
     {/*  <Card style={{ 
        borderRadius: 16, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
        border: '1px solid #e8e8e8', 
        overflow: 'hidden', 
        transition: 'all 0.3s ease-in-out',
        marginBottom: 24 
      }}>
        <CardContent>
          <Grid container spacing={24} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search tours, clients, or tour names..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                style={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 25,
                    backgroundColor: 'white'
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="processed">Processed</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="From Date"
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                type="date"
                label="To Date"
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpdate}
                startIcon={<RefreshIcon />}
                fullWidth
                disabled={loading}
                style={{
                  borderRadius: 25,
                  padding: '8px 24px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease-in-out'
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card> */}

      {/* Tabla de tours mejorada y responsiva */}
      <Card style={{ 
        borderRadius: 16, 
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)', 
        border: '1px solid #e8e8e8', 
        overflow: 'hidden', 
        transition: 'all 0.3s ease-in-out'
      }}>
        <CardContent style={{ position: 'relative', padding: 0 }}>
          {/* Header de la tabla */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: 24,
            padding: '0 24px',
            flexWrap: 'wrap',
            gap: 16
          }}>
            <Typography variant="h6" style={{ color: '#2c3e50', fontWeight: 600 }}>
              Pending Tour Bookings
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ExportIcon />}
              onClick={handleExport}
              disabled={tours.length === 0}
              style={{
                borderRadius: 25,
                padding: '8px 24px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease-in-out'
              }}
            >
              EXPORT
            </Button>
          </div>

          {/* Overlay de carga */}
          {loading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255,255,255,0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1
            }}>
              <CircularProgress size={60} />
            </div>
          )}

          {/* Contenedor responsivo para la tabla */}
          <div style={{ 
            overflowX: 'auto',
            overflowY: 'hidden',
            borderRadius: 12,
            border: '1px solid #e0e0e0'
          }}>
            <Table style={{ 
              minWidth: 1200, // Ancho mínimo para asegurar que todas las columnas sean visibles
              tableLayout: 'fixed' // Mejora el rendimiento con tablas grandes
            }}>
              <TableHead style={{
                backgroundColor: '#f8f9fa',
                position: 'sticky',
                top: 0,
                zIndex: 2
              }}>
                <TableRow>
                  <TableCell style={{ 
                    minWidth: 100, 
                    maxWidth: 120,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Tour ID
                  </TableCell>
                  <TableCell style={{ 
                    minWidth: 100, 
                    maxWidth: 120,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Booking ID
                  </TableCell>
                  <TableCell style={{ 
                    minWidth: 200, 
                    maxWidth: 350,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Tour Name
                  </TableCell>
                  <TableCell style={{ 
                    minWidth: 80, 
                    maxWidth: 100,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    P4M
                  </TableCell>
                  <TableCell style={{ 
                    minWidth: 120, 
                    maxWidth: 140,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Start Date
                  </TableCell>
                  <TableCell style={{ 
                    minWidth: 200, 
                    maxWidth: 300,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Client
                  </TableCell>
                  <TableCell style={{ 
                    minWidth: 120, 
                    maxWidth: 140,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Submitted
                  </TableCell>
                  <TableCell style={{ 
                    minWidth: 120, 
                    maxWidth: 140,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Days Left
                  </TableCell>
                 {/*  <TableCell style={{ 
                    minWidth: 100, 
                    maxWidth: 120,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Status
                  </TableCell> */}
               {/*    <TableCell style={{ 
                    minWidth: 100, 
                    maxWidth: 120,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Price
                  </TableCell> */}
                  <TableCell style={{ 
                    minWidth: 80, 
                    maxWidth: 100,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Details
                  </TableCell>
                  <TableCell style={{ 
                    minWidth: 80, 
                    maxWidth: 100,
                    padding: '12px 8px',
                    fontWeight: 600,
                    color: '#2c3e50',
                    borderBottom: '2px solid #e9ecef',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Cancel
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <div style={{ textAlign: 'center', padding: '32px 0' }}>
                        <ErrorIcon color="error" style={{ fontSize: 48, marginBottom: 16 }} />
                        <Typography color="error" variant="h6" gutterBottom>
                          {error}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handleUpdate}
                          startIcon={<RefreshIcon />}
                        >
                          Try Again
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : tours.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10}>
                      <div style={{
                        padding: 64,
                        textAlign: 'center',
                        color: '#6c757d'
                      }}>
                        <InfoIcon style={{ fontSize: 64, marginBottom: 16, opacity: 0.5 }} />
                        <Typography variant="h6" gutterBottom>
                          No tours found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Try adjusting your search filters or refresh the data
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  tours.map((tour) => {
                    const tourRentId = getNestedValue(tour, 'id')
                    const bookingId = getNestedValue(tour, 'booking_info.id') || getNestedValue(tour, 'booking_info.booking_id')
                    const tourName = getNestedValue(tour, 'tour_info.tour_name')
                    // Obtener p4m correctamente: 1 o true = Yes, 0 o false = No
                    const p4mValue = tour.booking_info && (tour.booking_info.p4m !== null && tour.booking_info.p4m !== undefined) 
                      ? tour.booking_info.p4m 
                      : false
                    const p4m = p4mValue === 1 || p4mValue === true
                    const tourStartDate = getNestedValue(tour, 'tour_info.tour_start_date')
                    const clientName = getNestedValue(tour, 'client_info.name')
                    const submittedDate = getNestedValue(tour, 'booking_info.submitted_date')
                    const status = getNestedValue(tour, 'rental_info.status')
                    const price = getNestedValue(tour, 'rental_info.rental_price', 0)
                    const startDate = getNestedValue(tour, 'rental_info.start_date')
                    // console.log(startDate)
                    const dayActual = moment()
                    const daysLeft = moment(startDate).diff(dayActual, "days")

                    return (
                      <TableRow key={tourRentId} style={{
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: '#f8f9ff'
                        }
                      }}>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <Typography variant="body2" style={{ fontWeight: 600, color: '#2c3e50' }}>
                            #{tourRentId}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <Typography variant="body2" color="textSecondary">
                            #{bookingId}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem',
                          maxWidth: '200px'
                        }}>
                          <Typography style={{
                            fontWeight: 600,
                            color: '#2c3e50',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.4'
                          }}>
                            {tourName}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <Chip
                            label={p4m ? 'Yes' : 'No'}
                            color={p4m ? 'primary' : 'default'}
                            size="small"
                            style={{
                              borderRadius: 20,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              padding: '4px 8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          />
                        </TableCell>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            color: '#6c757d'
                          }}>
                            <CalendarIcon fontSize="small" style={{ fontSize: '0.875rem' }} />
                            <Typography variant="body2">
                              {tourStartDate ? moment(tourStartDate).format('DD/MM/YYYY') : 'N/A'}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem',
                          minWidth: 200,
                          maxWidth: 300
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4
                          }}>
                            <Avatar style={{ width: 20, height: 20, fontSize: '0.75rem' }}>
                              <PersonIcon fontSize="small" />
                            </Avatar>
                            <Typography variant="body2" style={{
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              lineHeight: '1.4'
                            }}>
                              {clientName}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            color: '#6c757d'
                          }}>
                            <TimeIcon fontSize="small" style={{ fontSize: '0.875rem' }} />
                            <Typography variant="body2">
                              {submittedDate ? moment(submittedDate).format('DD/MM/YYYY') : 'N/A'}
                            </Typography>
                          </div>
                        </TableCell>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            color: '#6c757d'
                          }}>
                            <TimeIcon fontSize="small" style={{ fontSize: '0.875rem' }} />
                            <Typography variant="body2">
                              {daysLeft}
                            </Typography>
                          </div>
                        </TableCell>
                       {/*  <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <Chip
                            label={getStatusText(status)}
                            color={getStatusColor(status)}
                            size="small"
                            style={{
                              borderRadius: 20,
                              fontWeight: 600,
                              fontSize: '0.75rem',
                              padding: '4px 8px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px'
                            }}
                          />
                        </TableCell> */}
                       {/*  <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <Typography style={{
                            fontWeight: 600,
                            color: '#28a745',
                            fontSize: '0.875rem'
                          }}>
                            {formatPrice(price)}
                          </Typography>
                        </TableCell> */}
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <Tooltip title="View details" placement="top">
                            <IconButton 
                              size="small" 
                              onClick={() => handleViewDetails(tour)}
                              style={{ 
                                borderRadius: 8,
                                padding: 6,
                                transition: 'all 0.2s ease-in-out',
                                backgroundColor: '#e3f2fd'
                              }}
                            >
                              <VisibilityIcon color="primary" style={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell style={{ 
                          padding: '12px 8px',
                          borderBottom: '1px solid #f0f0f0',
                          fontSize: '0.875rem'
                        }}>
                          <Tooltip title="Cancel book tour" placement="top">
                            <IconButton 
                              size="small" 
                              onClick={() => onDeleteTour && onDeleteTour(tour)}
                              style={{ 
                                borderRadius: 8,
                                padding: 6,
                                transition: 'all 0.2s ease-in-out',
                                backgroundColor: '#ffebee'
                              }}
                            >
                              <CancelIcon color="error" style={{ fontSize: '1rem' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Paginación mejorada */}
      {pagination.total > pagination.limit && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <CustomPagination
            count={pagination.total}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
          />
        </div>
      )}
    </div>
  )
}

export default TourList 


// WEBPACK FOOTER //
// ./src/features/tours/components/TourList.js